import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Bot,
  UserPlus,
  Search,
  Clock,
  Zap,
  MessageSquare,
  Star,
  Activity,
  Hash,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { AppHeader } from "@/components/sartn/AppHeader";
import { FeatureStatusBanner } from "@/components/sartn/FeatureStatusBanner";
import { LoadingSkeleton } from "@/components/sartn/LoadingSkeleton";
import { EmptyState } from "@/components/sartn/EmptyState";
import { useAutomationStore } from "@/stores/automationStore";
import { useLanguageStore } from "@/stores/languageStore";
import type { Automation, AutomationType, AutomationScope } from "@/stores/automationStore";
import { cn } from "@/lib/cn";

const SPRING = { type: "spring" as const, stiffness: 300, damping: 30 };
const STAGGER_CARDS = 0.03;
const STAGGER_STATS = 0.04;

type TabFilter = "all" | "active" | "inactive";

const TYPE_ICONS: Record<AutomationType, LucideIcon> = {
  auto_reply: Bot,
  welcome: UserPlus,
  keyword: Search,
  scheduled: Clock,
  event: Zap,
  follow_up: MessageSquare,
  feedback: Star,
};

const TYPE_LABELS: Record<AutomationType, string> = {
  auto_reply: "Auto Reply",
  welcome: "Welcome",
  keyword: "Keyword",
  scheduled: "Scheduled",
  event: "Event",
  follow_up: "Follow-Up",
  feedback: "Feedback",
};

const TYPE_COLORS: Record<AutomationType, string> = {
  auto_reply: "bg-blue-50 text-blue-700",
  welcome: "bg-green-50 text-green-700",
  keyword: "bg-violet-50 text-violet-700",
  scheduled: "bg-amber-50 text-amber-700",
  event: "bg-cyan-50 text-cyan-700",
  follow_up: "bg-orange-50 text-orange-700",
  feedback: "bg-pink-50 text-pink-700",
};

const SCOPE_LABELS: Record<AutomationScope, string> = {
  private: "Private",
  group: "Group",
  channel: "Channel",
  all: "All",
};

const SCOPE_COLORS: Record<AutomationScope, string> = {
  private: "bg-slate-50 text-slate-700",
  group: "bg-indigo-50 text-indigo-700",
  channel: "bg-teal-50 text-teal-700",
  all: "bg-gray-100 text-gray-500",
};

function formatRelativeTime(isoDate: string | null): string {
  if (!isoDate) return "Never";
  const diff = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface MiniStatProps {
  icon: LucideIcon;
  label: string;
  value: string;
  accentColor: string;
  index: number;
}

function MiniStat({ icon: Icon, label, value, accentColor, index }: MiniStatProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * STAGGER_STATS, duration: 0.25 }}
      className="flex flex-col gap-1.5 rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-3 shadow-sm"
    >
      <div className={cn("flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)]", accentColor)}>
        <Icon className="h-4 w-4" />
      </div>
      <span className="font-headline text-lg font-bold text-[var(--color-text)]">{value}</span>
      <span className="text-xs font-medium text-[var(--color-text-secondary)]">{label}</span>
    </motion.div>
  );
}

function ToggleSwitch({ checked, onToggle }: { checked: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className={cn(
        "relative h-6 w-10 shrink-0 rounded-full transition-colors",
        checked ? "bg-[var(--color-primary)]" : "bg-[var(--color-border)]",
      )}
      role="switch"
      aria-checked={checked}
      aria-label={checked ? "Disable automation" : "Enable automation"}
    >
      <span className={cn(
        "absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform",
        checked && "translate-x-4",
      )} />
    </button>
  );
}

function AutomationCard({
  automation,
  index,
  onToggle,
}: {
  automation: Automation;
  index: number;
  onToggle: () => void;
}) {
  const language = useLanguageStore((state) => state.language);
  const TypeIcon = TYPE_ICONS[automation.automationType];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * STAGGER_CARDS, duration: 0.25 }}
      className={cn(
        "flex items-start gap-3 rounded-[var(--radius-lg)] shadow-sm",
        "bg-[var(--color-surface)] p-3 transition-colors",
        !automation.enabled && "opacity-60",
      )}
    >
      <div className={cn(
        "flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)]",
        TYPE_COLORS[automation.automationType],
      )}>
        <TypeIcon className="h-5 w-5" />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-[var(--color-text)]">
            {automation.name}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className={cn(
            "rounded-[var(--radius-full)] px-2 py-0.5 text-[10px] font-medium",
            TYPE_COLORS[automation.automationType],
          )}>
            {TYPE_LABELS[automation.automationType]}
          </span>
          <span className={cn(
            "rounded-[var(--radius-full)] px-2 py-0.5 text-[10px] font-medium",
            SCOPE_COLORS[automation.scope],
          )}>
            {SCOPE_LABELS[automation.scope]}
          </span>
        </div>

        <div className="flex items-center gap-3 text-xs text-[var(--color-text-muted)]">
          <span className="flex items-center gap-1">
            <Activity className="h-3 w-3" />
            {automation.statsTriggered} triggered
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatRelativeTime(automation.statsLastTriggered)}
          </span>
        </div>
        <span className="text-[10px] font-medium text-amber-700">
          {language === "am" ? "የዝርዝር ገጽ በመጠባበቅ ላይ" : "Details page pending"}
        </span>
      </div>

      <ToggleSwitch checked={automation.enabled} onToggle={onToggle} />
    </motion.div>
  );
}

function TabBar({
  activeTab,
  onTabChange,
  counts,
}: {
  activeTab: TabFilter;
  onTabChange: (tab: TabFilter) => void;
  counts: { all: number; active: number; inactive: number };
}) {
  const tabs: { key: TabFilter; label: string; count: number }[] = [
    { key: "all", label: "All", count: counts.all },
    { key: "active", label: "Active", count: counts.active },
    { key: "inactive", label: "Inactive", count: counts.inactive },
  ];

  return (
    <div className="mb-4 flex gap-1 rounded-[var(--radius-lg)] bg-[var(--color-surface-raised)] p-1">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onTabChange(tab.key)}
          className={cn(
            "flex-1 rounded-[var(--radius-md)] px-3 py-1.5 text-xs font-medium transition-colors",
            activeTab === tab.key
              ? "bg-[var(--color-surface)] text-[var(--color-text)] shadow-sm"
              : "text-[var(--color-text-muted)]",
          )}
        >
          {tab.label} ({tab.count})
        </button>
      ))}
    </div>
  );
}

export default function AutomationsPage() {
  const { automations, isLoading, loadAutomations, toggleAutomation } = useAutomationStore();
  const [activeTab, setActiveTab] = useState<TabFilter>("all");

  useEffect(() => {
    loadAutomations();
  }, [loadAutomations]);

  function navigateToNewAutomation() {
    window.location.hash = "/automations/new";
  }

  const activeCount = automations.filter((a) => a.enabled).length;
  const inactiveCount = automations.filter((a) => !a.enabled).length;
  const totalTriggers = automations.reduce((sum, a) => sum + a.statsTriggered, 0);

  const filteredAutomations = automations.filter((a) => {
    if (activeTab === "active") return a.enabled;
    if (activeTab === "inactive") return !a.enabled;
    return true;
  });

  if (isLoading && automations.length === 0) {
    return (
      <div className="flex flex-1 flex-col">
        <AppHeader title="Automations" />
        <LoadingSkeleton variant="card" count={4} className="px-4 pt-4" />
      </div>
    );
  }

  if (!isLoading && automations.length === 0) {
    return (
      <div className="flex flex-1 flex-col">
        <AppHeader title="Automations" />
        <EmptyState
          icon={Zap}
          title="No automations yet"
          description="Start with a template to automate replies, follow-ups, and more."
          actionLabel="Create Automation"
          onAction={navigateToNewAutomation}
        />
      </div>
    );
  }

  const statCards = [
    { icon: Zap, label: "Total", value: String(automations.length), accentColor: "bg-blue-50 text-blue-600" },
    { icon: Activity, label: "Triggers", value: totalTriggers.toLocaleString(), accentColor: "bg-green-50 text-green-600" },
    { icon: Hash, label: "Active", value: String(activeCount), accentColor: "bg-amber-50 text-amber-600" },
  ];

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title="Automations" />

      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="flex-1 px-4 pb-24 pt-2"
      >
        <div className="mb-4 grid grid-cols-3 gap-3">
          {statCards.map((stat, index) => (
            <MiniStat key={stat.label} {...stat} index={index} />
          ))}
        </div>

        <TabBar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          counts={{ all: automations.length, active: activeCount, inactive: inactiveCount }}
        />

        <FeatureStatusBanner
          status="connected"
          title="Automations are backend-connected / አውቶሜሽኖች ከbackend ጋር ተገናኝተዋል"
          description="Create and toggle actions save through the API. Detail editing is still pending. / መፍጠርና ማብራት-ማጥፋት በAPI ይቀመጣሉ፤ የዝርዝር ማስተካከያ ገና ይቀራል።"
        />

        {filteredAutomations.length === 0 ? (
          <p className="py-8 text-center text-sm text-[var(--color-text-muted)]">
            No {activeTab} automations found.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {filteredAutomations.map((automation, index) => (
              <AutomationCard
                key={automation.id}
                automation={automation}
                index={index}
                onToggle={() => toggleAutomation(automation.id)}
              />
            ))}
          </div>
        )}
      </motion.main>

      <motion.button
        type="button"
        onClick={navigateToNewAutomation}
        whileTap={{ scale: 0.9 }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={SPRING}
        className="fixed bottom-24 right-4 z-20 flex h-14 w-14 items-center justify-center
                   rounded-full bg-[var(--color-primary)] text-white shadow-lg
                   active:bg-[var(--color-primary-hover)]"
        aria-label="Create new automation"
      >
        <Plus className="h-6 w-6" />
      </motion.button>
    </div>
  );
}
